import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Spinner, Collapse } from 'reactstrap';
import axios from 'axios';
import config from '../../config.js';
import { store } from '../../store/index.js';
import { getBlobUrlAndContentType } from '../../utils/getBlobUrlAndContentType.js';
import { FaPhone } from 'react-icons/fa';
import { toast } from 'react-toastify';
import NavBar from '../layout/NavBar';
import ModalConfirm from '../common/ModalConfirm';
import { clickToCallForOrder } from '../../services/api/get-labs-orders';
import { getOpsCallReasons } from '../pharmacy-orders/UploadRxComponents/Services';

// Styling constants
const BRAND = '#6C5DD3';

const C = {
    blue:   '#0EA5E9',
    green:  '#22C55E',
    red:    '#EF4444',
    amber:  '#F59E0B',
    slate:  '#64748B',
    border: '#E2E8F0',
    dark:   '#0f172a',
    violet: '#A78BFA',
    cyan:   '#22D3EE',
};

const sectionCardStyle = {
    border: '1px solid #e0daf8',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(108,93,211,0.08)',
    padding: '10px',
};

const primaryButtonStyle = {
    padding: '6px 14px',
    backgroundColor: BRAND,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
};

const statusBadge = (status) => {
    if (!status) return <span style={{ color: '#94a3b8' }}>—</span>;
    const s = String(status).toLowerCase();
    let bg, color, dot;
    if (s.includes('not_triggered') || s.includes('not triggered')) {
        bg = '#ede9fe'; color = '#5b21b6'; dot = C.violet;
    } else if (s.includes('success') || s.includes('completed') || s.includes('done') || s.includes('answered')) {
        bg = '#dcfce7'; color = '#15803d'; dot = C.green;
    } else if (s.includes('fail') || s.includes('error') || s.includes('reject') || s.includes('missed') || s.includes('busy')) {
        bg = '#fee2e2'; color = '#b91c1c'; dot = C.red;
    } else if (s.includes('pending') || s.includes('process') || s.includes('initiated')) {
        bg = '#fef3c7'; color = '#92400e'; dot = C.amber;
    } else {
        bg = '#f1f5f9'; color = '#475569'; dot = C.slate;
    }
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: bg, color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: dot, display: 'inline-block' }} />
            {status}
        </span>
    );
};

// API Functions
const apiRequest = async (url, method = 'GET', data = null) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    const state = store.getState();
    if (state.user.token) {
        headers.Authorization = state.user.token;
    }

    try {
        const response = await axios({ url, method, data, headers });
        return response.data;
    } catch (err) {
        const errorMessage = err.response && err.response.data && err.response.data.message || err.message;
        throw new Error(errorMessage);
    }
};

const getScanAndPayCartDetailByCartId = async (cartId) => {
    return await apiRequest(`${config.pharmacyService}/scan-and-pay/fetch-cart-detail?cartId=${cartId}&isSecureDocumentUrl=1`);
};

const getCallList = async (cartId) => {
    return await apiRequest(`${config.pharmacyService}/scan-and-pay/fetch-call-list?cartId=${cartId}`);
};

const getOrderList = async (cartId) => {
    return await apiRequest(`${config.pharmacyService}/scan-and-pay/fetch-order-list?cartId=${cartId}`);
};

const getUpiScanList = async (cartId) => {
    return await apiRequest(`${config.pharmacyService}/scan-and-pay/fetch-upi-scan-list?cartId=${cartId}`);
};

const getCaseComments = async (cartId) => {
    return await apiRequest(`${config.pharmacyService}/scan-and-pay/fetch-case-comments?cartId=${cartId}`);
};

const addCaseComment = async (cartId, comment) => {
    return await apiRequest(`${config.pharmacyService}/scan-and-pay/add-case-comment`, 'POST', { cartId, comment });
};

const updateCartApprovedAmount = async (cartId, approvedAmount) => {
    return await apiRequest(`${config.pharmacyService}/scan-and-pay/update-cart-approved-amount`, 'POST', { cartId, approvedAmount });
};

class PharmacyScanAndPayCartDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            cartId: this.props.match.params.cartId,
            cartDetails: {
                items: { covered: [], uncovered: [] },
                totalAmount: null,
                requestedAmount: null,
                approvedAmount: null,
                prescriptionUrl: '',
                cartId: null,
            },
            approvedAmount: null,
            error: '',
            loading: true,
            updating: false,
            callList: [],
            orderList: [],
            upiScanList: [],
            caseComments: [],
            callListLoading: true,
            orderListLoading: true,
            upiScanListLoading: true,
            caseCommentsLoading: true,
            newCaseComment: '',
            postingComment: false,
            isCommentsOpen: true,
            callReasons: [],
            callProcessing: false,
            callConfirmModal: {
                isOpen: false,
                message: 'Initiate a call between you and the user?',
                isOpsCallForPharmacy: false,
                opsCallReasons: [],
                onConfirm: () => {},
                onClose: () => {},
            },
            contextData: {
                digitisationRequestId: '',
                userName: '',
                userPhone: ''
            }
        };
    }

    componentDidMount() {
        // Extract context data from localStorage
        const { cartId } = this.state;
        const savedData = localStorage.getItem(`scan-and-pay-cart-${cartId}`);
        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData);
                this.setState({
                    contextData: {
                        digitisationRequestId: parsedData.digitisationRequestId || '—',
                        userName: parsedData.userName || '—',
                        userPhone: parsedData.userPhone || '—'
                    }
                });
            } catch (e) {
                console.error("Failed to parse context data from localStorage", e);
            }
        }

        this.fetchCallReasons();
        this.fetchAllData();
    }

    fetchAllData = () => {
        const { cartId } = this.state;
        if (cartId) {
            this.fetchCartDetails(cartId);
            this.fetchOrderList(cartId);
            this.fetchCallList(cartId);
            this.fetchUpiScanList(cartId);
            this.fetchCaseComments(cartId);
        }
    };

    fetchCartDetails = async (cartId) => {
        this.setState({ loading: true, error: '' });
        try {
            const data = await getScanAndPayCartDetailByCartId(cartId);
            if (data.success) {
                if (data.cart && data.cart.prescriptionUrl) {
                    const state = store.getState();
                    if (state.user.token) {
                        data.cart.prescriptionUrl = await getBlobUrlAndContentType({
                            fileUrl: data.cart.prescriptionUrl,
                            headers: {Authorization: state.user.token}
                        }).then(res => res.blobUrl);
                    }
                }
                this.setState({ cartDetails: data.cart });
            } else {
                this.setState({ error: data.message });
            }
        } catch (err) {
            this.setState({ error: err.message });
        } finally {
            this.setState({ loading: false });
        }
    };

    fetchOrderList = async (cartId) => {
        this.setState({ orderListLoading: true });
        try {
            const data = await getOrderList(cartId);
            this.setState({ orderList: data.orderList || [] });
        } catch (err) {
            console.error('Error fetching order list:', err);
        } finally {
            this.setState({ orderListLoading: false });
        }
    };

    fetchCallList = async (cartId) => {
        this.setState({ callListLoading: true });
        try {
            const data = await getCallList(cartId);
            this.setState({ callList: data.callList || [] });
        } catch (err) {
            console.error('Error fetching call list:', err);
        } finally {
            this.setState({ callListLoading: false });
        }
    };

    fetchUpiScanList = async (cartId) => {
        this.setState({ upiScanListLoading: true });
        try {
            const data = await getUpiScanList(cartId);
            this.setState({ upiScanList: data.upiScanList || [] });
        } catch (err) {
            console.error('Error fetching upi scan list:', err);
        } finally {
            this.setState({ upiScanListLoading: false });
        }
    };

    fetchCaseComments = async (cartId) => {
        this.setState({ caseCommentsLoading: true });
        try {
            const data = await getCaseComments(cartId);
            this.setState({ caseComments: data.comments || [] });
        } catch (err) {
            console.error('Error fetching case comments:', err);
        } finally {
            this.setState({ caseCommentsLoading: false });
        }
    };

    postCaseComment = async () => {
        const { cartId, newCaseComment } = this.state;
        if (!newCaseComment || !newCaseComment.trim()) return;

        this.setState({ postingComment: true, error: '' });
        try {
            const response = await addCaseComment(cartId, newCaseComment.trim());
            if (response.success) {
                this.setState({ newCaseComment: '' });
                await this.fetchCaseComments(cartId);
            } else {
                this.setState({ error: response.message });
            }
        } catch (err) {
            this.setState({ error: err.message });
        } finally {
            this.setState({ postingComment: false });
        }
    };

    handleUpdateApprovedAmount = async () => {
        const { cartId, approvedAmount } = this.state;
        if (!approvedAmount || approvedAmount <= 0) {
            this.setState({ error: "Please enter a valid approved amount." });
            return;
        }

        this.setState({ updating: true, error: '' });
        try {
            const response = await updateCartApprovedAmount(cartId, approvedAmount);
            if (response.success) {
                await this.fetchCartDetails(cartId);
                toast.success('Approved amount updated successfully');
            } else {
                this.setState({ error: response.message });
            }
        } catch (err) {
            this.setState({ error: err.message });
        } finally {
            this.setState({ updating: false, approvedAmount: null });
        }
    };

    fetchCallReasons = async () => {
        try {
            const callReasons = await getOpsCallReasons();
            this.setState({ callReasons });
        } catch (error) {
            console.error('Error fetching call reasons:', error);
        }
    };

    initiateCall = (orderId, phone, reasonForCalling) => {
        if (this.state.callProcessing) return;
        this.setState({ callProcessing: true });

        clickToCallForOrder(orderId, phone, 'pharmacy-order', reasonForCalling)
            .then((response) => {
                this.setState({ callProcessing: false });
                if (response.body.message === 'success') {
                    toast.success('Call initiated');
                } else {
                    toast.error('Unable to initiate call');
                }
            })
            .catch((err) => {
                this.setState({ callProcessing: false });
                console.error('Unable to initiate call', err);
                toast.error(`Unable to initiate call - ${err.response?.data?.errorMessage || err.message}`);
            });
    };

    openCallConfirmModal = (orderId, phone) => {
        this.setState({
            callConfirmModal: {
                isOpen: true,
                message: 'Initiate a call between you and the user?',
                isOpsCallForPharmacy: true,
                opsCallReasons: this.state.callReasons,
                onConfirm: (selectedCallReason) => {
                    this.initiateCall(orderId, phone, selectedCallReason);
                    return Promise.resolve();
                },
                onClose: () => {
                    this.setState({
                        callConfirmModal: { isOpen: false, onConfirm: () => {}, onClose: () => {} },
                    });
                },
            },
        });
    };

    toggleComments = () => this.setState({ isCommentsOpen: !this.state.isCommentsOpen });

    render() {
        const {
            cartId,
            cartDetails,
            error,
            loading,
            updating,
            orderListLoading,
            callListLoading,
            upiScanListLoading,
            orderList,
            callList,
            upiScanList,
            caseComments,
            caseCommentsLoading,
            newCaseComment,
            postingComment,
            isCommentsOpen,
            callConfirmModal,
            contextData,
        } = this.state;

        const realCommentCount = caseComments.filter((comment) => !comment.isSystem).length;

        return (
            <div className="container-fluid px-0" style={{ paddingTop: '60px' }}>
                <NavBar />
                <section className="sp-root" style={{ width: '100%', padding: '16px', boxSizing: 'border-box', fontWeight: 600 }}>
                    <style>{`
                        .sp-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
                        .sp-scroll::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 3px; }
                        .sp-scroll::-webkit-scrollbar-thumb { background: #94a3b8 !important; border-radius: 3px; }
                        .sp-scroll::-webkit-scrollbar-thumb:hover { background: #64748b !important; }
                        section * { font-weight: inherit; }
                    `}</style>

                    <div style={{ padding: '16px 20px', background: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 4px 14px rgba(15,23,42,0.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <h4 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Scan and Pay Cart Details</h4>
                        </div>

                        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fecaca', fontSize: '13px' }}>{error}</div>}

                        {/* Context bar */}
                        <div className="sp-context-bar" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '10px 16px', background: '#eff6ff', border: `1px solid #bfdbfe`, borderLeft: `4px solid ${C.blue}`, borderRadius: '8px', marginBottom: '16px' }}>
                            <span style={{ fontSize: '12px', color: C.slate, whiteSpace: 'nowrap' }}>Viewing details for</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: C.blue, whiteSpace: 'nowrap' }}>Cart #{cartId}</span>
                            <span style={{ fontSize: '12px', color: C.slate, whiteSpace: 'nowrap' }}>Digitisation ID</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap' }}>{contextData.digitisationRequestId}</span>
                            <span style={{ fontSize: '12px', color: C.slate, minWidth: 0, wordBreak: 'break-word' }}>{contextData.userName}</span>
                            <span style={{ fontSize: '12px', color: C.slate, whiteSpace: 'nowrap' }}>Phone</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap' }}>{contextData.userPhone}</span>
                            <button
                                type="button"
                                onClick={() => this.openCallConfirmModal(orderList[0] && orderList[0].orderId, contextData.userPhone)}
                                style={{
                                    marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: 0, border: 'none', background: 'transparent',
                                    fontSize: '13px', fontWeight: '700', color: C.red, cursor: 'pointer', whiteSpace: 'nowrap',
                                }}
                            >
                                <FaPhone size={12} /> Call User
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {/* Order List */}
                            <div className="order-list">
                                {orderListLoading ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>
                                ) : (
                                    <div style={{ ...sectionCardStyle, backgroundColor: '#fff' }}>
                                        <h6 style={{ margin: '0 0 12px', padding: '8px 12px', borderLeft: `4px solid ${C.blue}`, background: '#f8fafc', borderRadius: '0 6px 6px 0', color: '#0f172a', fontWeight: '700', fontSize: '13px', letterSpacing: '0.2px' }}>
                                            Order List {orderList.length > 0 && <span style={{ marginLeft: '8px', background: C.blue, color: '#fff', borderRadius: '10px', padding: '1px 8px', fontSize: '11px' }}>{orderList.length}</span>}
                                        </h6>
                                        {orderList.length === 0 ? (
                                            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No orders found</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '6px' }} className="sp-scroll">
                                                {orderList.map((order, index) => (
                                                    <div key={index} style={{ flex: '0 0 280px', background: '#f8fafc', border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.blue}`, borderRadius: '8px', padding: '12px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                            <span style={{ fontSize: '15px', fontWeight: '800', color: C.blue }}>Order #{order.orderId}</span>
                                                            {statusBadge(order.payoutTransactionStatus)}
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                            {[
                                                                { label: 'Placed On',       value: order.placedOn },
                                                                { label: 'Payout Amount',   value: order.payoutAmount ? `₹${order.payoutAmount}` : '—', color: C.green },
                                                                { label: 'Wallet Deduction', value: order.currentWalletDeduction ? `₹${order.currentWalletDeduction}` : '—', color: C.red },
                                                                { label: 'UTR',             value: order.utr, color: C.blue },
                                                                { label: 'Message',         value: order.payoutTransactionMessage },
                                                            ].map(({ label, value, color }) => (
                                                                <div key={label} style={{ background: '#fff', borderRadius: '6px', padding: '6px 10px', border: `1px solid ${C.border}` }}>
                                                                    <div style={{ fontSize: '10px', color: C.slate, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>{label}</div>
                                                                    <div style={{ fontSize: '12px', fontWeight: '600', color: color || '#0f172a' }}>{value || '—'}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Call History */}
                            <div className="call-list">
                                {callListLoading ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>
                                ) : (
                                    <div style={{ ...sectionCardStyle, backgroundColor: '#fff' }}>
                                        <h6 style={{ margin: '0 0 12px', padding: '8px 12px', borderLeft: `4px solid ${C.cyan}`, background: '#f8fafc', borderRadius: '0 6px 6px 0', color: '#0f172a', fontWeight: '700', fontSize: '13px', letterSpacing: '0.2px' }}>
                                            Call History {callList.length > 0 && <span style={{ marginLeft: '8px', background: C.cyan, color: '#fff', borderRadius: '10px', padding: '1px 8px', fontSize: '11px' }}>{callList.length}</span>}
                                        </h6>
                                        {callList.length === 0 ? (
                                            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No call history found</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '6px' }} className="sp-scroll">
                                                {callList.map((call, index) => (
                                                    <div key={index} style={{ flex: '0 0 260px', background: '#f8fafc', border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.cyan}`, borderRadius: '8px', padding: '12px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{call.opsUserName || (call.callStatus === 'NOT_TRIGGERED' ? 'No Call Placed' : '—')}</span>
                                                            {statusBadge(call.callStatus)}
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                            {[
                                                                { label: 'Phone',      value: call.opsUserPhone },
                                                                { label: 'Duration',   value: call.duration, color: C.cyan },
                                                                { label: 'Start Time', value: call.startTime },
                                                                { label: 'End Time',   value: call.endTime },
                                                                ...(call.providerStatus ? [{ label: 'Provider', value: call.providerStatus }] : []),
                                                                ...(call.reason ? [{ label: 'Reason', value: call.reason, color: C.amber }] : []),
                                                                ...(call.eligibleFrom ? [{ label: 'Eligible From', value: call.eligibleFrom, color: C.amber }] : []),
                                                            ].map(({ label, value, color }) => (
                                                                <div key={label} style={{ background: '#fff', borderRadius: '6px', padding: '6px 10px', border: `1px solid ${C.border}` }}>
                                                                    <div style={{ fontSize: '10px', color: C.slate, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>{label}</div>
                                                                    <div style={{ fontSize: '12px', fontWeight: '600', color: color || '#0f172a' }}>{value || '—'}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* UPI Scan List */}
                            <div className="upi-scan">
                                {upiScanListLoading ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>
                                ) : (
                                    <div style={{ ...sectionCardStyle, backgroundColor: '#fff' }}>
                                        <h6 style={{ margin: '0 0 12px', padding: '8px 12px', borderLeft: `4px solid ${C.violet}`, background: '#f8fafc', borderRadius: '0 6px 6px 0', color: '#0f172a', fontWeight: '700', fontSize: '13px', letterSpacing: '0.2px' }}>
                                            Scanned UPI List {upiScanList.length > 0 && <span style={{ marginLeft: '8px', background: C.violet, color: '#fff', borderRadius: '10px', padding: '1px 8px', fontSize: '11px' }}>{upiScanList.length}</span>}
                                        </h6>
                                        {upiScanList.length === 0 ? (
                                            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No UPI scans found</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '6px' }} className="sp-scroll">
                                                {upiScanList.map((item, index) => (
                                                    <div key={index} style={{ flex: '0 0 240px', background: '#f8fafc', border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.violet}`, borderRadius: '8px', padding: '12px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                            <span style={{ fontSize: '13px', fontWeight: '700', color: C.violet, wordBreak: 'break-all' }}>{item.upiId || '—'}</span>
                                                            {statusBadge(item.status)}
                                                        </div>
                                                        <div style={{ background: '#fff', borderRadius: '6px', padding: '6px 10px', border: `1px solid ${C.border}` }}>
                                                            <div style={{ fontSize: '10px', color: C.slate, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>Message</div>
                                                            <div style={{ fontSize: '12px', color: '#475569' }}>{item.message || '—'}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Cart Details Section */}
                            <div className="cart-details">
                                {loading ? (
                                    <div style={{ ...sectionCardStyle, backgroundColor: '#fff', textAlign: 'center', padding: '20px' }}>
                                        <Spinner />
                                    </div>
                                ) : (
                                    <div style={{ ...sectionCardStyle, backgroundColor: '#fff' }}>
                                        <h6 style={{ margin: '0 0 12px', padding: '8px 12px', borderLeft: `4px solid ${C.slate}`, background: '#f8fafc', borderRadius: '0 6px 6px 0', color: '#0f172a', fontWeight: '700', fontSize: '13px', letterSpacing: '0.2px' }}>
                                            Cart Details
                                        </h6>
                                        {!cartId ? (
                                            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No cart selected</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '6px' }} className="sp-scroll">
                                                {/* Summary card */}
                                                <div style={{ flex: '0 0 220px', background: '#f8fafc', border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.blue}`, borderRadius: '8px', padding: '12px 14px' }}>
                                                    <div style={{ fontSize: '15px', fontWeight: '800', color: C.blue, marginBottom: '10px' }}>Cart #{cartDetails?.cartId || cartId}</div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
                                                        {[
                                                            { label: 'Total Amount',     value: cartDetails?.totalAmount ? `₹${cartDetails.totalAmount}` : '—',       color: C.blue },
                                                            { label: 'Requested Amount', value: cartDetails?.requestedAmount ? `₹${cartDetails.requestedAmount}` : '—', color: C.amber },
                                                            { label: 'Approved Amount',  value: cartDetails?.approvedAmount != null ? `₹${cartDetails.approvedAmount}` : '—', color: C.green },
                                                        ].map(({ label, value, color }) => (
                                                            <div key={label} style={{ background: '#fff', borderRadius: '6px', padding: '6px 10px', border: `1px solid ${C.border}` }}>
                                                                <div style={{ fontSize: '10px', color: C.slate, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>{label}</div>
                                                                <div style={{ fontSize: '13px', fontWeight: '700', color }}>{value}</div>
                                                            </div>
                                                        ))}
                                                        {cartDetails?.prescriptionUrl && (
                                                            <a href={cartDetails.prescriptionUrl} target="_blank" rel="noopener noreferrer"
                                                                style={{ display: 'block', textAlign: 'center', padding: '6px 10px', background: C.violet, color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none', marginTop: '2px' }}>
                                                                View Prescription
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Covered items */}
                                                <div style={{ flex: '0 0 220px', background: '#f0fdf4', border: `1px solid #bbf7d0`, borderLeft: `4px solid ${C.green}`, borderRadius: '8px', padding: '12px 14px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                        <span style={{ fontSize: '12px', fontWeight: '700', color: C.green, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Covered Items</span>
                                                        <span style={{ background: '#dcfce7', color: C.green, borderRadius: '10px', padding: '1px 8px', fontSize: '11px', fontWeight: '600' }}>{cartDetails?.items?.covered?.length || 0}</span>
                                                    </div>
                                                    {cartDetails?.items?.covered?.length > 0 ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            {cartDetails.items.covered.map((item, index) => (
                                                                <div key={index} style={{ background: '#fff', borderRadius: '6px', padding: '6px 10px', border: '1px solid #bbf7d0' }}>
                                                                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#166534', marginBottom: '2px' }}>{item.name}</div>
                                                                    <div style={{ fontSize: '11px', color: C.slate }}>₹{item.mrp} × {item.quantity}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div style={{ fontSize: '12px', color: '#86efac' }}>No covered items</div>
                                                    )}
                                                </div>

                                                {/* Uncovered items */}
                                                <div style={{ flex: '0 0 220px', background: '#fff5f5', border: `1px solid #fecaca`, borderLeft: `4px solid ${C.red}`, borderRadius: '8px', padding: '12px 14px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                        <span style={{ fontSize: '12px', fontWeight: '700', color: C.red, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Uncovered Items</span>
                                                        <span style={{ background: '#fee2e2', color: C.red, borderRadius: '10px', padding: '1px 8px', fontSize: '11px', fontWeight: '600' }}>{cartDetails?.items?.uncovered?.length || 0}</span>
                                                    </div>
                                                    {cartDetails?.items?.uncovered?.length > 0 ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            {cartDetails.items.uncovered.map((item, index) => (
                                                                <div key={index} style={{ background: '#fff', borderRadius: '6px', padding: '6px 10px', border: '1px solid #fecaca' }}>
                                                                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#991b1b', marginBottom: '2px' }}>{item.name}</div>
                                                                    <div style={{ fontSize: '11px', color: C.slate }}>₹{item.mrp} × {item.quantity}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div style={{ fontSize: '12px', color: '#fca5a5' }}>No uncovered items</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Update Approved Amount */}
                                <div style={{ ...sectionCardStyle, backgroundColor: '#fff', marginTop: '0' }}>
                                    <h6 style={{ margin: '0 0 12px', padding: '8px 12px', borderLeft: `4px solid ${C.green}`, background: '#f8fafc', borderRadius: '0 6px 6px 0', color: '#0f172a', fontWeight: '700', fontSize: '13px', letterSpacing: '0.2px' }}>
                                        Update Approved Amount
                                    </h6>
                                    <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '6px' }} className="sp-scroll">
                                        <div style={{ flex: '0 0 320px', background: '#f8fafc', border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.green}`, borderRadius: '8px', padding: '12px 14px' }}>
                                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                <input
                                                    type="number"
                                                    value={this.state.approvedAmount ?? ''}
                                                    onChange={(e) => this.setState({ approvedAmount: e.target.value })}
                                                    placeholder="Enter new approved amount"
                                                    className="form-control"
                                                    style={{ flex: 1, borderColor: '#e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                                                />
                                                <button
                                                    onClick={this.handleUpdateApprovedAmount}
                                                    style={{ ...primaryButtonStyle, backgroundColor: C.green, whiteSpace: 'nowrap' }}
                                                    disabled={updating}
                                                >
                                                    {updating ? <Spinner size="sm" /> : 'Update Amount'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Comments */}
                            <div className="case-comments" style={{ marginTop: '16px' }}>
                                <div style={{ ...sectionCardStyle, backgroundColor: '#fff' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 12px' }}>
                                        <h6 style={{ margin: 0, padding: '8px 12px', borderLeft: `4px solid ${C.amber}`, background: '#f8fafc', borderRadius: '0 6px 6px 0', color: '#0f172a', fontWeight: '700', fontSize: '13px', letterSpacing: '0.2px' }}>
                                            Comments {realCommentCount > 0 && <span style={{ marginLeft: '8px', background: C.amber, color: '#fff', borderRadius: '10px', padding: '1px 8px', fontSize: '11px' }}>{realCommentCount}</span>}
                                        </h6>
                                        <button
                                            onClick={this.toggleComments}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#F8FAFC', color: '#334155', border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', userSelect: 'none' }}
                                        >
                                            {isCommentsOpen ? '▲' : '▼'}
                                        </button>
                                    </div>

                                    <Collapse isOpen={isCommentsOpen}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                                            <textarea
                                                value={newCaseComment}
                                                onChange={(e) => this.setState({ newCaseComment: e.target.value })}
                                                placeholder="Add an internal note..."
                                                className="form-control"
                                                rows={3}
                                                style={{ borderColor: C.border, borderRadius: '8px', fontSize: '13px', resize: 'vertical' }}
                                            />
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Press Post to submit</span>
                                                <button
                                                    onClick={this.postCaseComment}
                                                    disabled={postingComment || !newCaseComment.trim()}
                                                    style={{ ...primaryButtonStyle, opacity: postingComment || !newCaseComment.trim() ? 0.6 : 1, cursor: postingComment || !newCaseComment.trim() ? 'not-allowed' : 'pointer' }}
                                                >
                                                    {postingComment ? <Spinner size="sm" /> : 'Post Comment'}
                                                </button>
                                            </div>
                                        </div>

                                        {caseCommentsLoading && caseComments.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>
                                        ) : caseComments.length === 0 ? (
                                            <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No comments yet</div>
                                        ) : (
                                            <div>
                                                {[...caseComments].reverse().map((comment, index) => (
                                                    <div key={comment.id ?? index} style={comment.isSystem ? {
                                                        background: '#f8fafc', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '10px',
                                                    } : {
                                                        padding: '0 2px 10px', marginBottom: '10px', borderBottom: '1px solid #f1f5f9',
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: '12px', fontWeight: '700', color: comment.isSystem ? C.slate : '#0f172a' }}>{comment.author}</span>
                                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>· {comment.createdAt}</span>
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: '#334155', wordBreak: 'break-word' }}>{comment.content}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Collapse>
                                </div>
                            </div>
                        </div>

                        <ModalConfirm
                            message={callConfirmModal.message}
                            onConfirm={callConfirmModal.onConfirm}
                            onClose={callConfirmModal.onClose}
                            isOpen={callConfirmModal.isOpen}
                            isOpsCallForPharmacy={callConfirmModal.isOpsCallForPharmacy}
                            opsCallReasons={callConfirmModal.opsCallReasons}
                        />
                    </div>
                </section>
            </div>
        );
    }
}

export default connect(
    state => ({ authToken: state.user.authToken, user: state.user })
)(PharmacyScanAndPayCartDetails);
